/**
 * Sentry initialization for the vendor/business dashboard.
 *
 * Per-tenant DSN: reads `business_dsn` from the tenant's `sentry` integration
 * block. Each tenant runs 3 separate Sentry projects (Customer / Business /
 * Rider) — this app reads the Business project DSN only.
 *
 * Init timing: imported FIRST in main.tsx (before any other module) so
 * Sentry's global handlers catch downstream errors. DSN resolves from the
 * inline R2 shape on `window.__TENANT_CONFIG__`. If inline is empty, falls
 * back to the `cravo:integrations-ready` event fired by integrations-bootstrap.
 *
 * Fail-closed: no DSN configured = no-op. Same pattern as Maps / Stripe in
 * the vendor app — never falls back to a platform-level key.
 */

import * as Sentry from '@sentry/react'

let initialized = false

interface SentryBlock {
  enabled?: boolean
  business_dsn?: string
  environment?: string
  traces_sample_rate?: number | string
  replays_sample_rate?: number | string
  release_prefix?: string
}

function resolveSentryConfig(): SentryBlock | null {
  if (typeof window === 'undefined') return null
  try {
    const tc = window.__TENANT_CONFIG__ as
      | {
          sentry?: SentryBlock
          integrations?: Record<string, { enabled?: boolean; credentials?: SentryBlock } | undefined>
        }
      | undefined
    if (!tc) return null

    const inline = tc.integrations?.sentry
    if (inline && inline.enabled !== false && inline.credentials?.business_dsn) {
      return { ...inline.credentials, enabled: true }
    }
    if (tc.sentry && tc.sentry.enabled !== false && tc.sentry.business_dsn) {
      return tc.sentry
    }
    return null
  } catch {
    return null
  }
}

function parseRate(value: number | string | undefined, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.min(1, Math.max(0, value))
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number.parseFloat(value)
    if (Number.isFinite(n)) return Math.min(1, Math.max(0, n))
  }
  return fallback
}

function initFromConfig(cfg: SentryBlock): void {
  if (initialized) return
  if (!cfg.business_dsn) return
  initialized = true

  const tracesSampleRate = parseRate(cfg.traces_sample_rate, 0.05)
  const replaysSampleRate = parseRate(cfg.replays_sample_rate, 0)
  const release =
    cfg.release_prefix && typeof cfg.release_prefix === 'string'
      ? `${cfg.release_prefix}business-web`
      : undefined

  const integrations = replaysSampleRate > 0
    ? [Sentry.browserTracingIntegration(), Sentry.replayIntegration()]
    : [Sentry.browserTracingIntegration()]

  Sentry.init({
    dsn: cfg.business_dsn,
    environment: cfg.environment || 'production',
    release,
    tracesSampleRate,
    replaysSessionSampleRate: replaysSampleRate,
    replaysOnErrorSampleRate: replaysSampleRate,
    sendDefaultPii: false,
    integrations,
  })
}

const initialCfg = resolveSentryConfig()
if (initialCfg) {
  initFromConfig(initialCfg)
} else if (typeof window !== 'undefined') {
  window.addEventListener(
    'cravo:integrations-ready',
    () => {
      const cfg = resolveSentryConfig()
      if (cfg) initFromConfig(cfg)
    },
    { once: true },
  )
}

